import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/dist/query';
import dayjs from 'dayjs';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch } from '../../app/hooks';
import { auditLogSearchApi, useSearchAuditLogsQuery } from '../../app/services/account';
import AuditLogResultInfoCard from '../../common/components/AuditLogResultInfoCard';
import CustomPagination from '../../common/components/CustomPagination';
import DataTable from '../../common/components/DataTable';
import Loading from '../../common/components/Loading';
import PageHeader from '../../common/components/PageHeader';
import RoundedContainer from '../../common/components/RoundedContainer';
import DashboardLayout from '../../common/layouts/Dashboard';
import { parseEditContent, formatFieldValue } from '../../common/utils/auditLogUtils';
import { DefaultPageSize } from '../../common/utils/commonUtils';
import { addBreadcrumb, BreadcrumbType } from '../../common/utils/editBreadcrums';
import { getPageTitle } from '../../common/utils/pageUtils';
import { saveReportDataToFile } from '../../common/utils/reportUtils';
import { AuditLogItem, AuditLogResponse } from '../../types/AuditLog';
import { PageProps } from '../../types/PageProps';
import ErrorPage from '../error/ErrorPage';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    verticalAlign: 'top',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'top',
    fontSize: '16px',
  },
}));

export default function AuditLogResultPage({ breadcrumbs }: PageProps) {
  const { state } = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [openModal, setOpenModal] = useState<number | null>(null);
  // const [expandedRows, setExpandedRows] = useState<number[]>([]); // COMMENTED: Old expand/collapse approach
  const { audiLogData } = !!state && state;

  const tableRef = useRef<HTMLDivElement>(null);
  // const expandAuditLogRef = useRef<HTMLDivElement>(null); // COMMENTED: Old expand/collapse approach
  const [totalPages, setTotalPages] = useState<number>(0);
  const [displayedAuditLogData, setDisplayedAuditLogData] = useState<AuditLogResponse>();
  const searchOptions = {
    clinicAccountId: searchParams.get('clinicAccountId') || '',
    clinicSiteId: searchParams.get('clinicSiteId') || '',
    userId: searchParams.get('userId') || '',
    patientId: searchParams.get('patientId') || '',
    eventType: searchParams.get('eventType') || '',
    fromDate: searchParams.get('fromDate') || '',
    toDate: searchParams.get('toDate') || '',
    page: searchParams.get('page') || '1',
  };

  const baseParams =
    '?clinicAccountId=:clinicAccountId' +
    '&clinicSiteId=:clinicSiteId' +
    '&userId=:userId' +
    '&patientId=:patientId' +
    '&eventType=:eventType' +
    '&fromDate=:fromDate' +
    '&toDate=:toDate';

  const getNavBackUrl = () => {
    return generatePath(`/dashboard/audit-log/${baseParams}`, searchOptions);
  };
  const newBreadcrumb: BreadcrumbType = {
    text: t('auditLog.title'),
    link: getNavBackUrl() || `/dashboard/audit-log/`,
  };

  const currentPage = parseInt(searchOptions.page);
  // Query data
  const auditLogQuery = {
    clinicAccountId: searchOptions.clinicAccountId,
    clinicSiteId: searchOptions.clinicSiteId,
    userId: searchOptions.userId,
    patientId: searchOptions.patientId,
    eventType: searchOptions.eventType,
    fromDateString: searchOptions.fromDate || undefined,
    toDateString: searchOptions.toDate || undefined,
    timezoneOffset: new Date().getTimezoneOffset(),
    page: currentPage,
    pageSize: DefaultPageSize,
  };
  const {
    data: fetchedAuditLogData,
    error,
    isLoading,
  } = useSearchAuditLogsQuery(!audiLogData ? auditLogQuery : skipToken);

  const showPagingInfo = totalPages > 1;

  useEffect(() => {
    if (!searchOptions.eventType) {
      setPageErrors([t('auditLog.error.noEventType')]);
      setTimeout(() => {
        const url = generatePath(`/dashboard/audit-log`);
        navigate(url, { replace: true });
      }, 3000);
    }
  }, [navigate, searchOptions.eventType, t]);

  useEffect(() => {
    let total = 0;
    if (searchOptions.eventType) {
      if (fetchedAuditLogData) {
        total = fetchedAuditLogData.total;
        setDisplayedAuditLogData(fetchedAuditLogData);
        if (fetchedAuditLogData.auditLogs.length === 0) {
          setPageErrors([t('auditLog.error.noDataFound')]);
        }
      } else if (audiLogData) {
        total = audiLogData.total;
        setDisplayedAuditLogData(audiLogData);
      }
      setTotalPages(Math.ceil(total / DefaultPageSize));
    }
  }, [audiLogData, fetchedAuditLogData, searchOptions.eventType, t]);

  const updateSearchUrl = (updatedData: any) => {
    const params = {
      ...searchOptions,
      ...updatedData,
    };

    const url = generatePath(`/dashboard/audit-log-result/${baseParams}&page=:page`, params);
    navigate(url, { replace: true });
  };

  const onDownLoadReport = async () => {
    const auditLogDownloadDispatcher = dispatch(
      auditLogSearchApi.endpoints.searchAuditLogsForDownload.initiate({
        clinicAccountId: auditLogQuery.clinicAccountId,
        clinicSiteId: auditLogQuery.clinicSiteId,
        userId: auditLogQuery.userId,
        patientId: auditLogQuery.patientId,
        eventType: auditLogQuery.eventType,
        fromDateString: auditLogQuery.fromDateString || undefined,
        toDateString: auditLogQuery.toDateString || undefined,
        timezoneOffset: new Date().getTimezoneOffset(),
      })
    );

    auditLogDownloadDispatcher.then((result) => {
      if (typeof result !== 'undefined') {
        const filenameStartDate = dayjs(auditLogQuery.fromDateString, 'MM/DD/YYYY').format(
          'YYYY-MM-DD'
        );
        const filenameEndDate = dayjs(auditLogQuery.toDateString, 'MM/DD/YYYY').format(
          'YYYY-MM-DD'
        );
        const fileName = `audit_log_${filenameStartDate}_${filenameEndDate}.csv`;
        saveReportDataToFile(result?.data?.csvData, { type: 'text/csv' }, fileName);
      }
    });
  };

  const handlePageChange = (value: any) => {
    const newPage = parseInt(value);
    updateSearchUrl({ page: newPage });
  };

  const getFieldTranslations = (actionType: string, actionRole: string) => {
    // Map action types to their respective form translation paths
    if (actionType === 'editedAccount' && actionRole === 'clinicAccount') {
      return t('auditLog.editContent.editedAccount', { returnObjects: true }) as Record<
        string,
        string
      >;
    } else if (
      (actionType === 'edited' || actionType === 'editedViaAPI') &&
      actionRole === 'user'
    ) {
      return t('auditLog.editContent.editedUser', { returnObjects: true }) as Record<
        string,
        string
      >;
    } else if (actionType === 'edited' && actionRole === 'patient') {
      return t('auditLog.editContent.editedPatient', { returnObjects: true }) as Record<
        string,
        string
      >;
    } else if (
      (actionType === 'edited' || actionType === 'editedViaAPI') &&
      actionRole === 'site'
    ) {
      return t('auditLog.editContent.editedSite', { returnObjects: true }) as Record<
        string,
        string
      >;
    } else if (actionType === 'updatedProfile' && actionRole === 'profile') {
      return t('auditLog.editContent.updatedProfile', { returnObjects: true }) as Record<string, string>;
    } else if (actionType === 'editedPatientInterview') {
      // For patient interviews, return null (questions are already in the data)
      return null;
    }
    return t('auditLog.editContent.deactivate', { returnObjects: true }) as Record<string, string>;
  };

  const renderComparisonModal = () => {
    if (!openModal) return null;

    const item = displayedAuditLogData?.auditLogs?.find((i: AuditLogItem) => i.id === openModal);
    if (!item) return null;

    const parsedData = parseEditContent(item.editContent);
    if (!parsedData) return null;

    const { before, after } = parsedData;

    // Get field translations based on action type and role
    const fieldTranslations = getFieldTranslations(item.actionType, item.actionRole);

    // Get all keys from before and after objects
    const beforeKeys = Object.keys(before);
    const afterKeys = Object.keys(after);
    const allKeysSet = new Set(beforeKeys.concat(afterKeys));
    const allKeys = Array.from(allKeysSet);

    if (allKeys.length === 0) return null;

    return (
      <Dialog open={true} onClose={() => setOpenModal(null)} maxWidth="xl" fullWidth>
        <DialogTitle>{t('auditLog.editContent.comparison')}</DialogTitle>
        <DialogContent>
          <Table size="medium">
            <TableHead>
              <TableRow>
                {item.actionType !== 'editedPatientInterview' ? (
                  <StyledTableCell sx={{ maxWidth: '26%' }}>
                    {t('auditLog.editContent.field')}
                  </StyledTableCell>
                ) : (
                  <StyledTableCell sx={{ maxWidth: '26%' }}>
                    {t('auditLog.editContent.question')}
                  </StyledTableCell>
                )}
                <StyledTableCell sx={{ maxWidth: '37%' }}>
                  {t('auditLog.editContent.before')}
                </StyledTableCell>
                <StyledTableCell sx={{ maxWidth: '37%' }}>
                  {t('auditLog.editContent.after')}
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allKeys.map((key) => {
                // Skip if both before and after are missing
                if (before[key] === undefined && after[key] === undefined) return null;

                // Get the field label from translations or use the key as fallback
                let fieldLabel: any = fieldTranslations?.[key] || key;

                // If fieldLabel is an object (nested translation), try to extract a string value
                if (typeof fieldLabel === 'object' && fieldLabel !== null) {
                  // Try common properties for label extraction
                  fieldLabel = fieldLabel.title || fieldLabel.label || fieldLabel.name || key;
                }

                return (
                  <TableRow key={key}>
                    <StyledTableCell
                      sx={{ maxWidth: '26%' }}
                      dangerouslySetInnerHTML={{ __html: String(fieldLabel) }}
                    />
                    <StyledTableCell
                      sx={{ maxWidth: '37%' }}
                      dangerouslySetInnerHTML={{ __html: formatFieldValue(before[key]) }}
                    />
                    <StyledTableCell
                      sx={{ maxWidth: '37%' }}
                      dangerouslySetInnerHTML={{ __html: formatFieldValue(after[key]) }}
                    />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(null)} variant="contained">
            {t('auditLog.editContent.close')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const getHeader = () => {
    return [
      { text: t('auditLog.items.updatedAt'), sx: { width: '8%' } },
      { text: t('auditLog.items.user'), sx: { width: '8%' } },
      { text: t('auditLog.items.ip'), sx: { width: '7%' } },
      { text: t('auditLog.items.actionType'), sx: { width: '8%' } },
      { text: t('auditLog.items.accountName'), sx: { width: '8%' } },
      { text: t('auditLog.items.siteName'), sx: { width: '7%' } },
      { text: t('auditLog.items.userAffected'), sx: { width: '7%' } },
      { text: t('auditLog.items.patientId'), sx: { width: '7%' } },
      { text: t('auditLog.items.measurement'), sx: { width: '7%' } },
      { text: t('auditLog.items.interviewId'), sx: { width: '7%' } },
      { text: t('auditLog.items.externalInterviewId'), sx: { width: '7%' } },
      { text: t('auditLog.items.reason'), sx: { width: '7%' } },
      { text: t('auditLog.items.details'), sx: { width: '6%' } },
    ];
  };

  const getRowData = (item: AuditLogItem) => {
    const parsedEditContent = parseEditContent(item.editContent);

    let allKeys: string[] = [];
    if (parsedEditContent) {
      const { before, after } = parsedEditContent;

      // Get all keys from before and after objects
      const beforeKeys = Object.keys(before);
      const afterKeys = Object.keys(after);
      const allKeysSet = new Set(beforeKeys.concat(afterKeys));
      allKeys = Array.from(allKeysSet);
    }

    let affectedUserFullName = '';
    if (!!item.actionToExtID && !item.affectedUserLName && !item.affectedUserFName) {
      affectedUserFullName = item.actionToExtID;
    } else if (item.affectedUserLName || item.affectedUserFName) {
      affectedUserFullName = `${item.affectedUserLName}${
        item.affectedUserLName && item.affectedUserFName ? ', ' : ''
      }${item.affectedUserFName}`;
    } else affectedUserFullName = '';

    return [
      {
        component: (
          <Typography sx={{ fontSize: '12px!important' }}>{getDateTime(item.updatedAt)}</Typography>
        ),
      },
      {
        component: (
          <Typography
            sx={{ fontSize: '12px!important' }}
          >{`${item.actionByLName}, ${item.actionByFName}`}</Typography>
        ),
      },
      { component: <Typography sx={{ fontSize: '12px!important' }}>{item.ipAddress}</Typography> },
      { component: <Typography sx={{ fontSize: '12px!important' }}>{item.actionName}</Typography> },
      {
        component: <Typography sx={{ fontSize: '12px!important' }}>{item.accountName}</Typography>,
      },
      { component: <Typography sx={{ fontSize: '12px!important' }}>{item.siteName}</Typography> },
      {
        component: (
          <Typography sx={{ fontSize: '12px!important' }}>{affectedUserFullName}</Typography>
        ),
      },
      { component: <Typography sx={{ fontSize: '12px!important' }}>{item.patient}</Typography> },
      {
        component: <Typography sx={{ fontSize: '12px!important' }}>{item.measurement}</Typography>,
      },
      {
        component: (
          <Typography sx={{ fontSize: '12px!important' }}>
            {item.actionRole === 'schedule'
              ? ''
              : item.measurementId ?? item.interviewResult ?? item.trackerResult}
          </Typography>
        ),
      },
      {
        component: (
          <Typography sx={{ fontSize: '12px!important' }}>
            {item.externalInterviewId || ''}
          </Typography>
        ),
      },
      {
        component: <Typography sx={{ fontSize: '12px!important' }}>{item.reason || ''}</Typography>,
      },
      {
        component:
          parsedEditContent && allKeys.length ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => setOpenModal(item.id)}
              sx={{ fontSize: '11px', textTransform: 'none', padding: '4px 8px' }}
            >
              {t('auditLog.items.view')}
            </Button>
          ) : (
            <Typography sx={{ fontSize: '12px!important' }}></Typography>
          ),
      },
    ];
  };

  const getDateTime = (dateTime: string) => {
    const dateTimeArr = dateTime?.split(' ') || []; // original formatted value: 07/11/2022 12:00:33 am
    if (dateTimeArr?.length) {
      const date = dateTimeArr[0];
      dateTimeArr.shift();
      return (
        <>
          {date}
          <br />
          {dateTimeArr.join(' ')}
        </>
      );
    }
    return '';
  };

  const renderDesktopData = (data: AuditLogItem[]) => {
    return (
      <DataTable
        commonCellStyles={{
          [`&.${tableCellClasses.head}`]: {
            padding: '16px 8px',
            '&:first-of-type': {
              paddingLeft: '24px',
            },
            fontSize: '12px',
          },
          [`&.${tableCellClasses.body}`]: {
            padding: '16px 8px',
            '&:first-of-type': {
              paddingLeft: '24px',
            },
          },
          [`&.${tableCellClasses.body}:first-of-type`]: {
            maxWidth: 'none',
            wordBreak: 'normal',
          },
        }}
        headers={getHeader()}
        items={data.map((item) => getRowData(item))}
      />
    );
  };

  if (error) {
    return <ErrorPage statusCode={(error as FetchBaseQueryError).status} />;
  }
  return (
    <DashboardLayout breadcrumbs={addBreadcrumb(breadcrumbs!, newBreadcrumb)}>
      <Helmet>
        <title>{getPageTitle(t('report.usageReport.output'))}</title>
      </Helmet>
      <Container maxWidth="xl" disableGutters={true}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            py: 2,
            [theme.breakpoints.up('md')]: {
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            },
          }}
          ref={tableRef}
        >
          <PageHeader
            sx={{ mb: 2, mx: isDesktop ? 3 : 2 }}
            headerText={t('report.usageReport.output')}
            backUrl={getNavBackUrl()}
          />
        </Box>

        {isLoading ? (
          <Loading />
        ) : (
          <>
            <Box ref={tableRef}>
              <RoundedContainer sx={{ py: 2, mb: 2 }}>
                <AuditLogResultInfoCard
                  fromDate={searchOptions.fromDate || ''}
                  toDate={searchOptions.toDate || ''}
                  onDownLoadReport={onDownLoadReport}
                  disabled={totalPages === 0}
                />
              </RoundedContainer>

              {displayedAuditLogData && (
                <>
                  <Box sx={{ width: '100%' }}>
                    {displayedAuditLogData?.auditLogs.length > 0 && (
                      <>
                        {renderDesktopData(displayedAuditLogData?.auditLogs)}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: showPagingInfo ? 4 : 2,
                            borderBottomLeftRadius: '15px',
                            borderBottomRightRadius: '15px',
                            background: theme.xPalette.white,
                          }}
                        >
                          {showPagingInfo && (
                            <CustomPagination
                              changePage={(e, value) => handlePageChange(value)}
                              currentPage={currentPage}
                              pages={totalPages}
                              pageRef={tableRef}
                            />
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </>
              )}
            </Box>
            {pageErrors &&
              pageErrors.length > 0 &&
              pageErrors.map((error) => (
                <Alert key={error} severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              ))}
          </>
        )}

        {/* Comparison Modal */}
        {renderComparisonModal()}
      </Container>
    </DashboardLayout>
  );
}
